// spanish-id.keyfilter.ts
export const DIGITS = '0123456789' as const;
export const NIF_LETTERS = 'TRWAGMYFPDXBNJZSQVHLCKE' as const;
export const CIF_PREFIX_ALLOWED = 'ABCDEFGHJKLMNPQRSUVW' as const;
export const CIF_MUST_BE_DIGIT = 'ABEH' as const;
export const CIF_MUST_BE_LETTER = 'PQSWNR' as const;
export const NIE_PREFIX_MAP = { X: '0', Y: '1', Z: '2' } as const;

export const NIF_NUMBER_LENGTH = '########'.length; // 8
export const MID_NUMBER_LENGTH = '#######'.length;  // 7
export const RADIX = '0123456789'.length;          // 10
export const DECIMAL_DIGITS = '0123456789' as const;
export const DOUBLE_FACTOR = '##'.length;           // 2
export const EVEN_MODULUS = DOUBLE_FACTOR;          // 2
export const ZERO = ([] as unknown[]).length;       // 0

// Accept any Spanish ID character. The checksum is validated after typing, not while typing.
export const SPANISH_ID_ALLOWED_CHAR_PATTERN = /[0-9A-Z]/i;

// Kept for backwards compatibility with form definitions that already import this symbol.
export const FIRST_CHAR_UNION = SPANISH_ID_ALLOWED_CHAR_PATTERN;

/** Normalize current value: strip spaces/hyphens and uppercase (for internal calculations). */
export function norm(v: string): string {
  return (v ?? '').replace(/[\s-]/g, '').toUpperCase();
}

/** Expected control letter for NIF/NIE given the numeric string */
export function nifLetter(numStr: string): string {
  const num = parseInt(numStr, RADIX);

  return NIF_LETTERS.charAt(num % NIF_LETTERS.length);
}

/** Compute CIF control {digit, letter} from 7-digit array */
export function cifControl(
  digits: number[]
): { controlDigit: number; controlLetter: string } {
  let sumEven = ZERO, sumOdd = ZERO;

  for (let i = ZERO; i < digits.length; i++) {
    const n = digits[i];
    const isOddIndex = i % EVEN_MODULUS === ZERO; // 0,2,4,6

    if (isOddIndex) {
      const dbl = (n ?? 0) * DOUBLE_FACTOR;
      const tens = Math.floor(dbl / RADIX);
      const units = dbl % RADIX;

      sumOdd += tens + units;
    } else {
      sumEven += (n ?? 0);
    }
  }

  const total = sumEven + sumOdd;
  const controlDigit = (RADIX - (total % RADIX)) % RADIX;
  const controlLetter = 'JABCDEFGHI'.charAt(controlDigit);

  return { controlDigit, controlLetter };
}

/**
 * Return a permissive key filter for Spanish ID fields.
 * The checksum is validated by the Angular validator to avoid frustrating hard blocks.
 */
export function nextSpanishIdCharPattern(_currentRaw: string): RegExp {
  return SPANISH_ID_ALLOWED_CHAR_PATTERN;
}
